import { useCallback, useEffect, useRef, useState } from "react";
import { ImageIcon, Link2, Pencil, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl } from "@/lib/api";
import { fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import SquareCropModal from "@/components/SquareCropModal";

export default function ProductForm({ batchId, product, open, onOpenChange, onSaved, batchRegion, forOrder }) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [orderedQty, setOrderedQty] = useState("1");
  const [sellingPrice, setSellingPrice] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [locMode, setLocMode] = useState("select");
  const [newLocName, setNewLocName] = useState("");
  const [newLocLink, setNewLocLink] = useState("");
  const [locSaving, setLocSaving] = useState(false);
  const [editLocOpen, setEditLocOpen] = useState(false);
  const [editLocName, setEditLocName] = useState("");
  const [editLocLink, setEditLocLink] = useState("");
  const [notes, setNotes] = useState("");
  const [photoMode, setPhotoMode] = useState("upload");
  const [photoPath, setPhotoPath] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [crop, setCrop] = useState(null);
  const [masterSel, setMasterSel] = useState(null);
  const [masterOrig, setMasterOrig] = useState(null);
  const [typedQuery, setTypedQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locSelectOpen, setLocSelectOpen] = useState(false);
  const [showAllLocs, setShowAllLocs] = useState(false);
  const [searchAllCountries, setSearchAllCountries] = useState(false);
  const fileRef = useRef(null);

  const canEditLocations = user.role === "owner";
  const selectedLocation = locations.find((l) => l.name === location && l.managed);
  const visibleLocations = showAllLocs ? locations : locations.filter((l) => !batchRegion || l.region === batchRegion);

  const loadLocations = useCallback(async () => {
    try {
      const { data } = await api.get("/locations");
      const rank = (l) => (l.region === batchRegion ? 0 : !l.region ? 1 : 2);
      data.sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
      setLocations(data);
    } catch {}
  }, [batchRegion]);

  useEffect(() => {
    if (open) {
      setName(product?.name || "");
      setOrderedQty(product ? String(product.ordered_qty) : "1");
      setSellingPrice(product?.selling_price != null ? String(product.selling_price) : "");
      setLocation(product?.buying_location || "");
      setLocMode("select");
      setEditLocOpen(false);
      setNotes(product?.notes || "");
      setPhotoPath(product?.photo_path || "");
      setPhotoUrl(product?.photo_url || "");
      setPhotoMode(product?.photo_url && !product?.photo_path ? "url" : "upload");
      setMasterSel(null);
      setMasterOrig(null);
      setTypedQuery("");
      setSuggestions([]);
      setShowAllLocs(false);
      setSearchAllCountries(false);
      if (product?.master_product_id) {
        api.get(`/masters/${product.master_product_id}`).then((r) => { setMasterSel(r.data); setMasterOrig(r.data.id); }).catch(() => {});
      }
      loadLocations();
    }
  }, [open, product, loadLocations]);

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    setLocSaving(true);
    try {
      const { data } = await api.post("/locations", { name: newLocName.trim(), link: newLocLink.trim() || null, region: batchRegion || null });
      await loadLocations();
      setLocation(data.name);
      setLocMode("select");
      toast.success(`Location "${data.name}" saved and selected`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLocSaving(false);
    }
  };

  const saveLocationEdit = async () => {
    if (!selectedLocation || !editLocName.trim()) return;
    setLocSaving(true);
    try {
      const { data } = await api.patch(`/locations/${selectedLocation.id}`, { name: editLocName.trim(), link: editLocLink.trim() || null });
      await loadLocations();
      setLocation(data.name);
      setEditLocOpen(false);
      toast.success("Location updated — existing product assignments kept");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLocSaving(false);
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(apiError(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      const c = document.createElement("canvas");
      c.width = bmp.width;
      c.height = bmp.height;
      c.getContext("2d").drawImage(bmp, 0, 0);
      const normBlob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.92));
      if (!normBlob) throw new Error("decode failed");
      setCrop({ url: URL.createObjectURL(normBlob), w: c.width, h: c.height, canvas: c });
    } catch {
      toast.error("Could not read this photo. Please choose a JPEG/PNG image.");
    }
  };

  const onCropConfirm = async (file) => {
    if (crop) URL.revokeObjectURL(crop.url);
    setCrop(null);
    await uploadFile(file);
  };

  const onCropCancel = () => {
    if (crop) URL.revokeObjectURL(crop.url);
    setCrop(null);
  };

  const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();

  // Master Product suggestions — search fires while typing, creation happens only on save
  useEffect(() => {
    if (masterSel) { setSuggestions([]); return; }
    const qq = name.trim();
    if (qq.length < 2 || (product && qq === product.name)) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const params = { q: qq };
        if (batchRegion && !searchAllCountries) params.country = batchRegion;
        const { data } = await api.get("/masters", { params });
        setSuggestions(data.results || []);
      } catch {
        setSuggestions([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [name, masterSel, product, batchRegion, searchAllCountries]);

  const pickMaster = (m) => {
    setTypedQuery(name.trim()); // the admin's own wording — learned as an alias on save
    setMasterSel(m);
    setName(m.canonical_name);
    setSuggestions([]);
    if (m.photo_path) { setPhotoMode("upload"); setPhotoPath(m.photo_path); setPhotoUrl(""); }
    else if (m.photo_url) { setPhotoMode("url"); setPhotoUrl(m.photo_url); setPhotoPath(""); }
    const countryLocs = (m.locations_by_country && m.locations_by_country[batchRegion]) || [];
    let prefill = countryLocs.length ? countryLocs[0] : null;
    if (!prefill && m.default_location) {
      const known = locations.find((l) => l.name === m.default_location);
      if (!known || !known.region || known.region === batchRegion) prefill = m.default_location; // skip locations belonging to another country
    }
    if (prefill) { setLocation(prefill); setLocMode("select"); }
    if (!sellingPrice && m.last_selling_price != null) setSellingPrice(String(m.last_selling_price));
  };

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    const qtyNum = parseInt(orderedQty, 10) || 0;
    const payload = {
      name: name.trim(),
      ordered_qty: forOrder ? 0 : qtyNum,
      selling_price: sellingPrice === "" ? null : parseFloat(sellingPrice),
      buying_location: location || null,
      notes,
      photo_path: photoMode === "upload" ? photoPath || null : null,
      photo_url: photoMode === "url" ? photoUrl || null : null,
      ...(product
        ? user.role === "owner" && (masterSel?.id || null) !== masterOrig
          ? { master_product_id: masterSel?.id || null } // owner-only change/unlink; null unlinks (master itself is never deleted)
          : {}
        : {
            ...(masterSel ? { master_product_id: masterSel.id } : {}),
            ...(masterSel && typedQuery && normName(typedQuery) !== normName(masterSel.canonical_name) ? { learn_alias: typedQuery } : {}),
          }),
    };
    try {
      const { data } = product ? await api.patch(`/products/${product.id}`, payload) : await api.post(`/batches/${batchId}/products`, payload);
      toast.success(product ? "Product updated" : "Product added");
      onOpenChange(false);
      onSaved(data, qtyNum);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const preview = photoMode === "upload" ? (photoPath ? fileUrl(photoPath) : null) : photoUrl || null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent
        onEscapeKeyDown={(e) => {
          if (locSelectOpen) {
            e.preventDefault();
            setLocSelectOpen(false);
          }
        }}
      >
        <DrawerHeader>
          <DrawerTitle className="font-heading">{product ? "Edit Product" : "Add Product"}</DrawerTitle>
        </DrawerHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 pb-8">
          <div className="space-y-2">
            <Label>Product name</Label>
            <Input data-testid="product-name-input" className="h-12" value={name} onChange={(e) => { setName(e.target.value); if (masterSel) setMasterSel(null); }} placeholder="e.g. SK-II Facial Treatment Essence" />
          </div>
          {masterSel && (
            <div data-testid="master-linked-chip" className="flex items-center justify-between gap-2 rounded-md border border-primary/40 bg-accent px-3 py-2">
              <div className="min-w-0 text-xs">
                <p className="truncate font-bold">Product Master: {masterSel.canonical_name}</p>
                {masterSel.last_selling_price != null && <p className="text-muted-foreground">Previous selling price: {fmtIDR(masterSel.last_selling_price)}</p>}
                {masterSel.last_buying_price != null && (
                  <p className="text-muted-foreground">
                    Last bought: {Number(masterSel.last_buying_price).toLocaleString("en-US")} {masterSel.last_buying_currency || ""}
                    {masterSel.last_buying_location ? ` · ${masterSel.last_buying_location}` : ""}
                  </p>
                )}
              </div>
              <button type="button" data-testid="master-unlink-button" className="shrink-0 text-xs font-bold text-destructive" onClick={() => setMasterSel(null)}>
                Unlink
              </button>
            </div>
          )}
          {!masterSel && suggestions.length > 0 && (
            <div className="space-y-1.5" data-testid="master-suggestions">
              <p className="text-xs font-semibold text-muted-foreground">Possible existing products:</p>
              {suggestions.map((m) => (
                <button key={m.id} type="button" data-testid={`master-suggestion-${m.id}`} className="flex w-full items-center gap-2 rounded-md border p-2 text-left transition-colors hover:bg-accent" onClick={() => pickMaster(m)}>
                  {m.photo_path || m.photo_url ? (
                    <img src={m.photo_path ? fileUrl(m.photo_path) : m.photo_url} alt="" className="h-10 w-10 shrink-0 rounded-md border object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-[#EDE4DE]">
                      <ImageIcon className="h-4 w-4 text-stone-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{m.canonical_name}</p>
                    {m.matched_alias && <p className="truncate text-xs text-muted-foreground">Previously known as: {m.matched_alias}</p>}
                    {m.countries?.length > 0 && <p className="truncate text-[10px] font-semibold text-stone-400">{m.countries.join(" · ")}</p>}
                  </div>
                  <span className="shrink-0 text-xs font-bold text-primary">Use This Product</span>
                </button>
              ))}
              <p className="text-xs text-muted-foreground">Different product? Keep going and save — a new Master is created only when you save.</p>
            </div>
          )}
          {!masterSel && batchRegion && name.trim().length >= 2 && !(product && name.trim() === product.name) && (
            <button
              type="button"
              data-testid="master-search-all-toggle"
              className="text-xs font-semibold text-primary"
              onClick={() => setSearchAllCountries(!searchAllCountries)}
            >
              {searchAllCountries ? `← Show ${batchRegion} products only` : "Not listed? Search all countries"}
            </button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Customer ordered qty</Label>
              <NumInput
                data-testid="product-qty-input"
                mode="int"
                className="h-12"
                value={orderedQty}
                onValue={setOrderedQty}
              />
            </div>
            <div className="space-y-2">
              <Label>Selling price (IDR)</Label>
              <NumInput
                data-testid="product-price-input"
                mode="money"
                className="h-12"
                placeholder="0"
                value={sellingPrice}
                onValue={setSellingPrice}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Buying location</Label>
            {locMode === "select" ? (
              <>
                <Select
                  open={locSelectOpen}
                  onOpenChange={setLocSelectOpen}
                  value={location || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__add__") {
                      setNewLocName("");
                      setNewLocLink("");
                      setLocMode("add");
                    } else {
                      setLocation(v === "__none__" ? "" : v);
                      setEditLocOpen(false);
                    }
                  }}
                >
                  <SelectTrigger data-testid="product-location-select" className="h-12">
                    <SelectValue placeholder="Select location (empty = UNASSIGNED)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="location-option-none">UNASSIGNED</SelectItem>
                    {visibleLocations.map((l) => (
                      <SelectItem key={l.name} value={l.name} data-testid={`location-option-${l.name.toLowerCase().replace(/\s/g, "-")}`}>
                        {l.name}
                      </SelectItem>
                    ))}
                    {location && !locations.some((l) => l.name === location) && (
                      <SelectItem value={location} data-testid="location-option-current">{location}</SelectItem>
                    )}
                    {canEditLocations && <SelectItem value="__add__" data-testid="location-option-add">+ Add New Buying Location</SelectItem>}
                  </SelectContent>
                </Select>
                {batchRegion && (
                  <button
                    type="button"
                    data-testid="location-show-all-toggle"
                    className="text-xs font-semibold text-primary"
                    onClick={() => setShowAllLocs(!showAllLocs)}
                  >
                    {showAllLocs ? `Show ${batchRegion} locations only` : "Show all locations"}
                  </button>
                )}
                {canEditLocations && selectedLocation && (
                  <button
                    type="button"
                    data-testid="location-edit-toggle"
                    className="flex items-center gap-1 text-xs font-semibold text-primary"
                    onClick={() => {
                      setEditLocName(selectedLocation.name);
                      setEditLocLink(selectedLocation.link || "");
                      setEditLocOpen(!editLocOpen);
                    }}
                  >
                    <Pencil className="h-3 w-3" /> Edit this location
                  </button>
                )}
                {editLocOpen && selectedLocation && (
                  <div className="space-y-2 rounded-md border bg-accent/30 p-3" data-testid="location-edit-form">
                    <Input data-testid="location-edit-name" className="h-11 bg-white" placeholder="Location name" value={editLocName} onChange={(e) => setEditLocName(e.target.value)} />
                    <Input data-testid="location-edit-link" className="h-11 bg-white" placeholder="Link / URL (optional)" value={editLocLink} onChange={(e) => setEditLocLink(e.target.value)} />
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" className="h-10 flex-1" data-testid="location-edit-cancel" onClick={() => setEditLocOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="button" className="h-10 flex-1" data-testid="location-edit-save" onClick={saveLocationEdit} disabled={locSaving || !editLocName.trim()}>
                        {locSaving ? "Saving…" : "Save Location"}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-2 rounded-md border bg-accent/30 p-3" data-testid="location-add-form">
                <Input data-testid="location-add-name" className="h-11 bg-white" placeholder="Location name, e.g. Costco" value={newLocName} onChange={(e) => setNewLocName(e.target.value)} />
                <Input data-testid="location-add-link" className="h-11 bg-white" placeholder="Link / URL (optional)" value={newLocLink} onChange={(e) => setNewLocLink(e.target.value)} />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" className="h-10 flex-1" data-testid="location-add-cancel" onClick={() => setLocMode("select")}>
                    Back
                  </Button>
                  <Button type="button" className="h-10 flex-1" data-testid="location-add-save" onClick={addLocation} disabled={locSaving || !newLocName.trim()}>
                    {locSaving ? "Saving…" : "Save & Select"}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Photo</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="photo-mode-upload"
                onClick={() => setPhotoMode("upload")}
                className={`flex h-11 items-center justify-center gap-2 rounded-md border text-sm font-semibold transition-colors ${
                  photoMode === "upload" ? "border-primary bg-accent text-accent-foreground" : "text-stone-600"
                }`}
              >
                <Upload className="h-4 w-4" /> Upload
              </button>
              <button
                type="button"
                data-testid="photo-mode-url"
                onClick={() => setPhotoMode("url")}
                className={`flex h-11 items-center justify-center gap-2 rounded-md border text-sm font-semibold transition-colors ${
                  photoMode === "url" ? "border-primary bg-accent text-accent-foreground" : "text-stone-600"
                }`}
              >
                <Link2 className="h-4 w-4" /> Image URL
              </button>
            </div>
            {photoMode === "upload" ? (
              <div>
                <input
                  ref={fileRef}
                  data-testid="photo-upload-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={onPhotoPicked}
                />
                <Button type="button" variant="outline" data-testid="photo-upload-button" className="h-12 w-full" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? "Uploading…" : photoPath ? "Replace photo" : "Choose photo"}
                </Button>
              </div>
            ) : (
              <Input
                data-testid="photo-url-input"
                className="h-12"
                placeholder="https://…"
                value={photoUrl}
                onChange={(e) => setPhotoUrl(e.target.value)}
              />
            )}
            {preview ? (
              <img src={preview} alt="Product" data-testid="photo-preview" className="h-28 w-28 rounded-md border object-cover" />
            ) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-md border bg-[#EDE4DE]">
                <ImageIcon className="h-8 w-8 text-stone-300" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea data-testid="product-notes-input" placeholder="Color, size, customer requests…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <Button data-testid="product-save-button" className="h-12 w-full text-base" onClick={submit} disabled={saving || uploading || !name.trim()}>
            {saving ? "Saving…" : product ? "Save Changes" : "Add Product"}
          </Button>
        </div>
        <SquareCropModal open={!!crop} imageUrl={crop?.url} natural={crop ? { w: crop.w, h: crop.h } : null} sourceCanvas={crop?.canvas} onConfirm={onCropConfirm} onCancel={onCropCancel} />
      </DrawerContent>
    </Drawer>
  );
}
